import Overlay from '@components/overlay/overlay';
import { usePreventScroll } from '@hooks/use-prevent-scroll';
import { useThrottledCallback } from '@hooks/use-throttled-callback';
import cn from 'classnames';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';

import styles from './navigation-drawer.module.scss';

interface NavigationDrawerProps {
  readonly open?: boolean;
}

/**
 * Drawer variant of the main menu
 */
const NavigationDrawer: React.FC<
  React.PropsWithChildren<NavigationDrawerProps>
> = ({ open = false, children }) => {
  usePreventScroll(open);

  //
  // Overlay is shown immediately but hidden only after a transition is finished.
  //
  const [overlayShown, setOverlayShown] = useState(false);
  useEffect(() => {
    if (open) {
      setOverlayShown(true);
    }
  }, [open]);

  //
  // We actually reveal the drawer with some delay. This is to prevent the
  // component from appearing with css classes already applied. The class gets
  // applied lates which allows a transition to play.
  //
  // Alternatively, reveal and hide animations could have been used. However
  // animations will not interpolate values from previously interrupted animations.
  //
  const [revealed, setRevealedState] = useState(false);
  const setRevealed = useThrottledCallback(
    (isRevealed: boolean) => setRevealedState(isRevealed),
    'doubleAnimationFrame',
  );
  useEffect(() => setRevealed(open), [setRevealed, open]);

  return (
    <>
      {open ? (
        <Head>
          <meta
            key="msapplication-TileColor"
            name="msapplication-TileColor"
            content={'#f1b600'}
          />
          <meta key="theme-color" name="theme-color" content={'#f1b600'} />
        </Head>
      ) : null}
      {overlayShown ? (
        <Overlay
          overlayKey="menu-drawer"
          className={cn(styles.overlay, { [styles.isOpen]: revealed })}
          onTransitionEnd={() => {
            if (!open) {
              setOverlayShown(false);
            }
          }}
        >
          <nav className={styles.drawer}>{children}</nav>
        </Overlay>
      ) : null}
    </>
  );
};

export default NavigationDrawer;
